/**
 * An event's courses on its map: each in its own colour from the course palette (brand.json's `course`, in the order
 * the payload lists them, primary first), on every basemap, the swap card's picture and the legend. An event with one
 * course keeps each map's own course style (cyan on Mapbox, ink in the world) and no legend.
 */

import { playTokens } from '@/lib/theme/playTokens'
import type { RouteBounds } from '@/lib/map/routeFocus'

/** One of the event's courses, as the viewer payload's `routes` list them. */
export interface CourseMapRoute {
  id?: string | null
  name?: string | null
  distance_km?: number | null
  gpx_url?: string | null
  bounds?: RouteBounds | null
  world_url?: string | null
}

/** A course drawn in its own colour, with what the legend says of it. */
export interface DrawnCourse {
  /** Stable for the course: its id, else its GPX link, else its place in the list. */
  key: string
  route: CourseMapRoute
  color: string
  /** The legend's short label: the distance its name gives, else its length, or its name when two share one. */
  label: string
  /** Its full name, for the legend's tooltip and accessible name. */
  name: string
  /**
   * Where it stacks among the courses, 0 at the bottom: the shortest on top, so where courses share a trail each shorter
   * one shows over the longer ones it runs along.
   */
  order: number
}

const PALETTE = [playTokens.course['1'], playTokens.course['2'], playTokens.course['3'], playTokens.course['4'], playTokens.course['5']]

/** The colour of the event's `index`th course (0 is the first): the course palette in order, then ink. */
export function courseColor(index: number): string {
  return PALETTE[index] ?? playTokens.color.ink
}

const kilometres = new Intl.NumberFormat('en', { maximumFractionDigits: 0 })

/** A distance in a course's name: "42 km" in "The Cobra Full 42 km", "65 km" in "Ultra 65K", "100 mi" in "100 Miler". */
const NAMED_DISTANCE = /(\d+(?:[.,]\d+)?)\s*(km|k|mi|miles?|miler)\b/i

/** The distance a course is known by, in kilometres: the one its name gives, else its GPX length; null with neither. */
export function courseKm(route: CourseMapRoute): number | null {
  const named = route.name?.match(NAMED_DISTANCE)
  if (named) {
    const value = Number(named[1].replace(',', '.'))
    return /^m/i.test(named[2]) ? value * 1.609344 : value
  }
  return typeof route.distance_km === 'number' && route.distance_km > 0 ? route.distance_km : null
}

/**
 * The distance a course is known by: the one its name gives (a race's 42 km course measures 41.1 on the GPX, and the
 * legend should say what the race says), else its GPX length.
 */
function distanceLabel(route: CourseMapRoute): string | null {
  const named = route.name?.match(NAMED_DISTANCE)
  if (named) return `${named[1]} ${/^m/i.test(named[2]) ? 'mi' : 'km'}`
  return typeof route.distance_km === 'number' && route.distance_km > 0 ? `${kilometres.format(route.distance_km)} km` : null
}

/**
 * The courses to draw each in its own colour: every course of the event when it has two or more, else null (one course
 * keeps the map's own style). Each keeps its colour by its place in the payload's list.
 */
export function drawnCourses(routes: readonly CourseMapRoute[] | null | undefined): DrawnCourse[] | null {
  const courses = (routes ?? []).filter((route) => route.id || route.gpx_url)
  if (courses.length < 2) return null
  const distances = courses.map(distanceLabel)
  // Longest at the bottom; a course of no known length below them all.
  const length = (route: CourseMapRoute) => (typeof route.distance_km === 'number' ? route.distance_km : Infinity)
  const stacked = courses.map((route, index) => ({ route, index })).sort((a, b) => length(b.route) - length(a.route) || a.index - b.index)
  const orders = new Map(stacked.map(({ index }, order) => [index, order]))
  return courses.map((route, index) => {
    const name = route.name?.trim() || distances[index] || `Course ${index + 1}`
    const distance = distances[index]
    const shared = distance !== null && distances.filter((other) => other === distance).length > 1
    return {
      key: route.id ?? route.gpx_url ?? String(index),
      route,
      color: courseColor(index),
      label: distance && !shared ? distance : name,
      name,
      order: orders.get(index) ?? 0,
    }
  })
}
